import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress,
  Typography,
  Card,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from '../theme/modernTheme';
import { showError } from '../utils/toast';
import { contactsAPI, donationsAPI, remindersAPI, transactionsAPI } from '../services/api';
import { formatDateForInput, parseDateString } from '../utils/dateUtils';
import {
  validateTransactionForm,
  parseBackendErrors,
  hasErrors as checkHasErrors,
} from '../utils/formValidation';
import {
  saveTransactionFormState,
  clearTransactionFormState,
  selectSavedTransactionFormState,
} from '../store/slices/transactionFormSlice';
import {
  selectDonationSchemas,
  selectDonationSchemasLoading,
} from '../store/slices/donationSchemasSlice';
import { selectGlobalTags, selectGlobalTagsStatus } from '../store/slices/tagsSlice';
import TagAutocomplete from './TagAutocomplete';
import { getFrequencyLabel } from '../utils/frequencyLabel';
import { trustMastersAPI } from '../services/api';
import { paymentMethodLabel } from '../utils/paymentMethodLabel';

const TransactionForm = forwardRef(function TransactionForm(
  { transactionId = null, reminderId = null, mode = 'edit', onSuccess, onCancel },
  ref,
) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const savedFormState = useSelector(selectSavedTransactionFormState);

  // Donation schemas from Redux (loaded in App.js on authentication)
  const donationSchemas = useSelector(selectDonationSchemas);
  const loadingSchemas = useSelector(selectDonationSchemasLoading);

  // Tags from Redux (loaded in App.js on authentication)
  const tags = useSelector(selectGlobalTags);
  const tagsStatus = useSelector(selectGlobalTagsStatus);

  const newContactId = searchParams.get('newContactId');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [pledgeSummary, setPledgeSummary] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsPage, setContactsPage] = useState(1);
  const [hasMoreContacts, setHasMoreContacts] = useState(true);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('');
  const [initialContactLoaded, setInitialContactLoaded] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [pledgeTagId, setPledgeTagId] = useState(null); // Store pledge tag ID to set after tags load
  const [tagsFromPledge, setTagsFromPledge] = useState(false); // Track if tags are inherited from pledge
  const [selectedDonationSchema, setSelectedDonationSchema] = useState(null);
  const [donationSchemaLocked, setDonationSchemaLocked] = useState(false); // Lock when from reminder

  // Trust Master state
  const [trustMasters, setTrustMasters] = useState([]);
  const [selectedTrustMaster, setSelectedTrustMaster] = useState(null);
  const [trustMasterLocked, setTrustMasterLocked] = useState(false); // Lock when from reminder/pledge
  const [loadingTrustMasters, setLoadingTrustMasters] = useState(false);
  const [trustMastersPage, setTrustMastersPage] = useState(1);
  const [hasMoreTrustMasters, setHasMoreTrustMasters] = useState(true);
  const [trustMasterSearchTerm, setTrustMasterSearchTerm] = useState('');
  const [debouncedTrustMasterSearch, setDebouncedTrustMasterSearch] = useState('');

  // Donor pledges state (for direct transactions only)
  const [donorPledges, setDonorPledges] = useState([]);
  const [loadingPledges, setLoadingPledges] = useState(false);
  const [selectedPledge, setSelectedPledge] = useState(null);

  // Track if transaction/reminder data has been loaded to prevent duplicate calls
  const [dataLoaded, setDataLoaded] = useState(false);

  const isViewMode = mode === 'view';

  const [formData, setFormData] = useState({
    contact_id: '',
    donation_id: '',
    donation_schema: '',
    donation_schema_name: '',
    trust_master_id: '',
    amount: '',
    payment_date: formatDateForInput(new Date()),
    payment_method: 'CASH',
    transaction_reference: '',
    bank_name: '',
    bank_account_number: '',
    notes: '',
    receipt_sent_via_email: false,
    receipt_sent_via_whatsapp: false,
    trust_name: '',
  });

  const [lockedFields, setLockedFields] = useState({
    contact_id: false,
    donation_id: false,
  });

  // Debounce contact search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactSearch(contactSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  // Debounce trust master search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTrustMasterSearch(trustMasterSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [trustMasterSearchTerm]);

  // Load contacts when debounced search changes
  useEffect(() => {
    // Skip loading contacts in view mode
    if (isViewMode) {
      return;
    }

    // Skip initial load if in edit mode and contact hasn't been loaded yet
    if (transactionId && !initialContactLoaded) {
      return;
    }

    // Reset to page 1 when search term changes
    setContactsPage(1);
    setHasMoreContacts(true);
    // Clear contacts list before loading new search results
    setContacts([]);
    loadContacts(1, debouncedContactSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedContactSearch, initialContactLoaded, isViewMode]);

  // Load trust masters when debounced search changes
  useEffect(() => {
    // Skip loading in view mode
    if (isViewMode) {
      return;
    }

    // Reset to page 1 when search term changes
    setTrustMastersPage(1);
    setHasMoreTrustMasters(true);
    // Clear trust masters list before loading new search results
    setTrustMasters([]);
    loadTrustMasters(1, debouncedTrustMasterSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTrustMasterSearch, isViewMode]);

  // Restore form state when returning from contact creation
  useEffect(() => {
    if (savedFormState && !transactionId) {
      setFormData(savedFormState.formData);
      setInitialContactLoaded(true);
    }
  }, [savedFormState, transactionId]);

  // Set tag from pledge once tags are loaded
  useEffect(() => {
    if (pledgeTagId && tags.length > 0 && selectedTags.length === 0) {
      // Handle array of tag IDs
      if (Array.isArray(pledgeTagId)) {
        const matchedTags = tags.filter((t) => pledgeTagId.includes(t.id));
        if (matchedTags.length > 0) {
          setSelectedTags(matchedTags);
        }
      } else {
        // Handle single tag ID (backward compatibility)
        const tag = tags.find((t) => t.id === pledgeTagId);
        if (tag) {
          setSelectedTags([tag]);
        }
      }
    }
  }, [pledgeTagId, tags]);

  // Set trust master once trust masters are loaded
  useEffect(() => {
    if (formData.trust_master_id && trustMasters.length > 0 && !selectedTrustMaster) {
      const trustMaster = trustMasters.find((tm) => tm.id === formData.trust_master_id);

      if (trustMaster) {
        setSelectedTrustMaster(trustMaster);
      } else if (trustMasters.length > 0) {
        // If trust master not found in loaded list, load it specifically
        loadSpecificTrustMaster(formData.trust_master_id);
      }
    }
  }, [formData.trust_master_id, trustMasters, selectedTrustMaster]);

  // Auto-uncheck receipt options when templates are not available
  useEffect(() => {
    if (selectedTrustMaster) {
      // Uncheck email receipt if no email template configured
      if (!selectedTrustMaster.emailTemplateId && formData.receipt_sent_via_email) {
        setFormData((prev) => ({
          ...prev,
          receipt_sent_via_email: false,
        }));
      }
      // Uncheck WhatsApp receipt if no WhatsApp template configured
      if (!selectedTrustMaster.whatsappTemplateId && formData.receipt_sent_via_whatsapp) {
        setFormData((prev) => ({
          ...prev,
          receipt_sent_via_whatsapp: false,
        }));
      }
    }
  }, [selectedTrustMaster, formData.receipt_sent_via_email, formData.receipt_sent_via_whatsapp]);

  // Auto-uncheck receipt options when contact doesn't have email/phone
  useEffect(() => {
    if (selectedContact) {
      // Uncheck email receipt if contact has no email
      if (!selectedContact.primary_email && formData.receipt_sent_via_email) {
        setFormData((prev) => ({
          ...prev,
          receipt_sent_via_email: false,
        }));
      }
      // Uncheck WhatsApp receipt if contact has no phone
      if (!selectedContact.phone_1 && formData.receipt_sent_via_whatsapp) {
        setFormData((prev) => ({
          ...prev,
          receipt_sent_via_whatsapp: false,
        }));
      }
    }
  }, [selectedContact, formData.receipt_sent_via_email, formData.receipt_sent_via_whatsapp]);

  // Handle newly created contact
  useEffect(() => {
    if (newContactId && savedFormState) {
      loadNewlyCreatedContact(newContactId);
    }
  }, [newContactId, savedFormState]);

  useEffect(() => {
    // Only load data once when component mounts with transactionId or reminderId
    if (dataLoaded) return;

    if (reminderId) {
      loadReminderDetails();
      setDataLoaded(true);
    } else if (transactionId) {
      loadTransactionDetails();
      setDataLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderId, transactionId, dataLoaded]);

  const loadContacts = async (pageNum = 1, searchTerm = '') => {
    try {
      setLoadingContacts(true);
      const params = {
        limit: 20,
        page: pageNum,
        sortBy: 'name',
        sortOrder: 'desc',
      };

      // Add search parameter if provided
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await contactsAPI.getAll(params);

      setContacts((prev) => {
        if (pageNum === 1) {
          // For first page or new search, replace all contacts
          return response.data;
        }
        // For pagination, append new contacts and filter duplicates
        const newContacts = response.data.filter(
          (newC) => !prev.some((existingC) => existingC.id === newC.id),
        );
        return [...prev, ...newContacts];
      });

      const total = response.pagination?.total || 0;
      const loadedCount = (pageNum - 1) * 20 + response.data.length;
      setHasMoreContacts(loadedCount < total);
      setContactsPage(pageNum);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      showError('Failed to load contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadTrustMasters = async (pageNum = 1, searchTerm = '') => {
    try {
      setLoadingTrustMasters(true);
      const params = {
        limit: 1000, // Large limit for dropdown
        page: pageNum,
        sortBy: 'trustName',
        sortOrder: 'asc',
      };

      // Add search parameter if provided
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await trustMastersAPI.getAll(params);

      setTrustMasters((prev) => {
        if (pageNum === 1) {
          // For first page or new search, replace all trust masters
          return response.data?.trustMasters || [];
        }
        // For pagination, append new trust masters and filter duplicates
        const newTrustMasters = (response.data?.trustMasters || []).filter(
          (newTM) => !prev.some((existingTM) => existingTM.id === newTM.id),
        );
        return [...prev, ...newTrustMasters];
      });

      const total = response.data?.pagination?.total || 0;
      const loadedCount = (pageNum - 1) * 1000 + (response.data?.trustMasters?.length || 0);
      setHasMoreTrustMasters(loadedCount < total);
      setTrustMastersPage(pageNum);
    } catch (error) {
      console.error('Failed to load trust masters:', error);
      showError('Failed to load trust masters');
    } finally {
      setLoadingTrustMasters(false);
    }
  };

  const loadSpecificTrustMaster = async (trustMasterId) => {
    try {
      const response = await trustMastersAPI.getById(trustMasterId);
      const trustMaster = response.data;

      // Add to trust masters list if not already present
      setTrustMasters((prev) => {
        const exists = prev.some((tm) => tm.id === trustMaster.id);
        if (exists) {
          return prev;
        }
        return [trustMaster, ...prev];
      });

      setSelectedTrustMaster(trustMaster);
    } catch (error) {
      console.error('Failed to load specific trust master:', error);
    }
  };

  const loadNewlyCreatedContact = async (contactId) => {
    try {
      setLoadingContacts(true);
      const contactResponse = await contactsAPI.getOne(contactId);
      const contact = contactResponse.data;

      // Set the selected contact
      setSelectedContact(contact);
      setFormData((prev) => ({
        ...prev,
        contact_id: contactId,
      }));

      // Add to contacts list if not already there
      setContacts((prev) => {
        const exists = prev.some((c) => c.id === parseInt(contactId));
        if (!exists) {
          return [contact, ...prev];
        }
        return prev;
      });

      // Clear saved form state
      dispatch(clearTransactionFormState());

      // Remove newContactId from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } catch (error) {
      console.error('Failed to load newly created contact:', error);
      showError('Failed to load the newly created contact');
      // Clear saved state anyway
      dispatch(clearTransactionFormState());
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleAddContact = () => {
    // Save current form state to Redux
    dispatch(
      saveTransactionFormState({
        formData,
      }),
    );

    // Navigate to contact creation page with "from=transaction" flag
    navigate('/contacts/new?from=transaction');
  };

  // Expose handleAddContact to parent via ref
  useImperativeHandle(ref, () => ({
    handleAddContact,
  }));

  const loadReminderDetails = async () => {
    try {
      setLoading(true);
      // Get reminder details
      const reminders = await remindersAPI.getAll();
      const reminder = reminders.data.find((r) => r.id === parseInt(reminderId));

      if (!reminder) {
        showError('Reminder not found');
        return;
      }

      // Get pledge summary
      const pledgeResponse = await donationsAPI.getOne(reminder.donation_id);
      setPledgeSummary({
        ...pledgeResponse.data,
        reminder_date: reminder.reminder_date, // Add reminder date to pledge summary
      });

      // Store pledge tags to be set after tags are loaded
      // Handle multiple tags from pledge
      if (
        pledgeResponse.data.tags &&
        Array.isArray(pledgeResponse.data.tags) &&
        pledgeResponse.data.tags.length > 0
      ) {
        // Set tags directly if they have full tag objects
        setSelectedTags(pledgeResponse.data.tags);
        setTagsFromPledge(true); // Mark tags as inherited from pledge
      } else if (
        pledgeResponse.data.tag_ids &&
        Array.isArray(pledgeResponse.data.tag_ids) &&
        pledgeResponse.data.tag_ids.length > 0
      ) {
        // Store tag IDs to be matched with loaded tags
        setPledgeTagId(pledgeResponse.data.tag_ids);
        setTagsFromPledge(true); // Mark tags as inherited from pledge
      } else if (pledgeResponse.data.tag_id) {
        // Fallback for single tag (backward compatibility)
        setPledgeTagId([pledgeResponse.data.tag_id]);
        setTagsFromPledge(true); // Mark tags as inherited from pledge
      } else {
        // No tags from pledge
        setTagsFromPledge(false);
      }

      // Auto-fill and lock fields
      setFormData((prev) => ({
        ...prev,
        contact_id: reminder.contact_id,
        donation_id: reminder.donation_id,
      }));

      setLockedFields({
        contact_id: true,
        donation_id: true,
      });

      // Lock donation_schema when from reminder (automatically taken from pledge)
      setDonationSchemaLocked(true);

      // Set selected donation schema
      if (pledgeResponse.data.donation_schema) {
        const schema = donationSchemas.find((s) => s.id === pledgeResponse.data.donation_schema);
        if (schema) {
          setSelectedDonationSchema(schema);
        }
      }

      // Lock trust_master when from reminder (automatically taken from pledge)
      setTrustMasterLocked(true);

      // Set selected trust master from pledge
      if (pledgeResponse.data.trust_master_id) {
        setFormData((prev) => ({
          ...prev,
          trust_master_id: pledgeResponse.data.trust_master_id,
          trust_name: pledgeResponse.data.trust_name || '',
        }));

        // Find and set the trust master object
        const trustMaster = trustMasters.find(
          (tm) => tm.id === pledgeResponse.data.trust_master_id,
        );

        if (trustMaster) {
          setSelectedTrustMaster(trustMaster);
        }
      }

      // Load the specific contact for this reminder
      if (reminder.contact_id) {
        try {
          const contactResponse = await contactsAPI.getOne(reminder.contact_id);
          const contact = contactResponse.data;

          // Set the selected contact in state
          setSelectedContact(contact);

          // Mark that initial contact is loaded so the search can proceed
          setInitialContactLoaded(true);
        } catch (error) {
          setInitialContactLoaded(true);
        }
      } else {
        setInitialContactLoaded(true);
      }
    } catch (error) {
      showError('Failed to load reminder details');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionDetails = async () => {
    try {
      setLoading(true);
      const response = await transactionsAPI.getOne(transactionId);
      const transaction = response.data;

      setFormData({
        contact_id: transaction.contact_id,
        donation_id: transaction.donation_id || '',
        donation_schema: transaction.donation_schema || '',
        trust_master_id: transaction.trust_master_id || '',
        amount: transaction.amount,
        payment_date: parseDateString(transaction.payment_date),
        payment_method: transaction.payment_method,
        transaction_reference: transaction.transaction_reference || '',
        bank_name: transaction.bank_name || '',
        bank_account_number: transaction.bank_account_number || '',
        notes: transaction.notes || '',
        receipt_sent_via_email: transaction.receipt_sent_via_email || false,
        receipt_sent_via_whatsapp: transaction.receipt_sent_via_whatsapp || false,
        donation_schema_name: transaction.donation_schema_name || '',
        trust_name: transaction.trust_name || '',
        created_by_name: transaction.created_by_name || '',
      });

      // Set selected donation schema if available
      if (transaction.donation_schema) {
        const schema = donationSchemas.find((s) => s.id === transaction.donation_schema);
        if (schema) {
          setSelectedDonationSchema(schema);
        }
      }

      // Set selected trust master if available
      if (transaction.trust_master_id) {
        const trustMaster = trustMasters.find((tm) => tm.id === transaction.trust_master_id);
        if (trustMaster) {
          setSelectedTrustMaster(trustMaster);
        }
      }

      // Set selected tags if available
      if (transaction.tags && Array.isArray(transaction.tags) && transaction.tags.length > 0) {
        // Use tags array directly from API response
        setSelectedTags(transaction.tags);
      } else if (transaction.tag_ids && Array.isArray(transaction.tag_ids)) {
        // Fallback: match tag_ids with loaded tags
        const transactionTags = tags.filter((t) => transaction.tag_ids.includes(t.id));
        if (transactionTags.length > 0) {
          setSelectedTags(transactionTags);
        }
      } else if (transaction.tag_id) {
        // Fallback: single tag for backward compatibility
        const tag = tags.find((t) => t.id === transaction.tag_id);
        if (tag) {
          setSelectedTags([tag]);
        } else if (transaction.tag_name) {
          // If tag not in array but we have tag_name from backend, create tag object
          setSelectedTags([
            {
              id: transaction.tag_id,
              name: transaction.tag_name,
              color: transaction.tag_color || null,
            },
          ]);
        }
      }

      // Lock contact_id and donation_id for editing
      setLockedFields({
        contact_id: true,
        donation_id: true,
      });

      // Lock donation schema ONLY if transaction is linked to a donation/pledge
      // If donation_id exists, it means transaction is from a pledge/reminder
      if (transaction.donation_id) {
        setDonationSchemaLocked(true);
        setTrustMasterLocked(true); // Also lock trust master
      } else {
        // Direct transaction - allow editing donation schema and trust master
        setDonationSchemaLocked(false);
        setTrustMasterLocked(false);
      }

      // In view mode, we can use the data from transaction response directly
      // No need to load contact and donation separately
      if (isViewMode) {
        // Create a contact object from transaction data for display
        if (transaction.contact_id) {
          const contactFromTransaction = {
            id: transaction.contact_id,
            first_name: transaction.first_name,
            last_name: transaction.last_name,
            organization_name: transaction.organization_name,
            type: transaction.first_name ? 'INDIVIDUAL' : 'ORGANIZATION',
          };
          setSelectedContact(contactFromTransaction);
        }

        // Create pledge summary from transaction data if available
        if (transaction.donation_id && transaction.pledge_amount !== undefined) {
          const collectedAmount = transaction.collected_amount || 0;
          setPledgeSummary({
            amount: transaction.pledge_amount || 0,
            collected_amount: collectedAmount,
            remaining_amount: (transaction.pledge_amount || 0) - collectedAmount,
            completion_percentage:
              transaction.pledge_amount > 0
                ? Math.round((collectedAmount / transaction.pledge_amount) * 100)
                : 0,
            reminder_date: transaction.reminder_date,
            trust_name: transaction.trust_name || 'N/A',
          });
        }

        setInitialContactLoaded(true);
        return; // Skip loading contact and donation in view mode
      }

      // Load the specific contact for this transaction (edit mode only)
      if (transaction.contact_id) {
        try {
          const contactResponse = await contactsAPI.getOne(transaction.contact_id);
          const contact = contactResponse.data;

          // Set the selected contact in state
          setSelectedContact(contact);

          // Mark that initial contact is loaded so the search can proceed
          setInitialContactLoaded(true);
        } catch (error) {
          setInitialContactLoaded(true);
        }
      } else {
        setInitialContactLoaded(true);
      }

      // Load pledge summary if linked to pledge (edit mode only)
      if (transaction.donation_id) {
        try {
          const pledgeResponse = await donationsAPI.getOne(transaction.donation_id);
          const pledgeData = pledgeResponse.data;

          // Use collected_amount from transaction response if available, otherwise calculate
          const collectedAmount =
            transaction.collected_amount !== undefined
              ? transaction.collected_amount
              : pledgeData.collected_amount || 0;

          // Calculate pledge summary from the donation data
          const summary = {
            amount: pledgeData.amount || 0,
            collected_amount: collectedAmount,
            remaining_amount: (pledgeData.amount || 0) - collectedAmount,
            completion_percentage:
              pledgeData.amount > 0 ? Math.round((collectedAmount / pledgeData.amount) * 100) : 0,
            trust_name: pledgeData.trust_name || 'N/A',
          };

          // Add reminder_date if available from transaction
          if (transaction.reminder_date) {
            summary.reminder_date = transaction.reminder_date;
          }

          setPledgeSummary(summary);

          // Store pledge tag ID if transaction doesn't have a tag
          if (pledgeData.tag_id && !transaction.tag_id) {
            setPledgeTagId(pledgeData.tag_id);
          }
        } catch (error) {}
      }
    } catch (error) {
      showError('Failed to load transaction details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    // Validate amount field - only allow positive integers
    if (field === 'amount') {
      // Allow empty value (user is clearing the field)
      if (value === '') {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: '' }));
        return;
      }

      // Check for invalid characters (anything other than digits)
      if (!/^\d+$/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          amount: 'Amount must contain only numbers (no decimals, hyphens, or special characters)',
        }));
        return; // Don't update the value
      }

      // Check if it's a valid positive integer
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue <= 0) {
        setErrors((prev) => ({
          ...prev,
          amount: 'Amount must be a positive number',
        }));
        return; // Don't update the value
      }

      // Check maximum amount (50 crores = 500,000,000)
      const MAX_AMOUNT = 500000000;
      if (numValue > MAX_AMOUNT) {
        setErrors((prev) => ({
          ...prev,
          amount: `Amount cannot exceed ₹50 crores (₹${MAX_AMOUNT.toLocaleString('en-IN')})`,
        }));
        return; // Don't update the value
      }

      // Check if amount exceeds remaining pledge amount (only when linked to a pledge)
      if (pledgeSummary && pledgeSummary.remaining_amount !== undefined) {
        const remainingAmount = pledgeSummary.remaining_amount || 0;
        if (numValue > remainingAmount) {
          // Update formData but keep the error
          setFormData((prev) => ({ ...prev, [field]: value }));
          setErrors((prev) => ({
            ...prev,
            amount: `Amount cannot exceed remaining pledge amount of ₹${remainingAmount.toLocaleString('en-IN')}`,
          }));
          return; // Don't clear error
        }
      }

      // Valid amount - clear error and update
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: '' }));
      return;
    }

    // For other fields, just update and clear error
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  // Load pledges for a donor (only for direct transactions)
  const loadDonorPledges = async (contactId) => {
    if (!contactId) {
      setDonorPledges([]);
      return;
    }

    try {
      setLoadingPledges(true);
      const response = await donationsAPI.getAll({
        contact_id: contactId,
        status: 'PENDING', // Only get pending pledges
      });
      setDonorPledges(response.data || []);
    } catch (error) {
      console.error('Failed to load donor pledges:', error);
      setDonorPledges([]);
    } finally {
      setLoadingPledges(false);
    }
  };

  // Handle pledge selection
  const handlePledgeSelect = async (pledge) => {
    if (!pledge) {
      // Clear pledge selection
      setSelectedPledge(null);
      setPledgeSummary(null);
      setFormData((prev) => ({
        ...prev,
        donation_id: '',
        donation_schema: '',
        trust_master_id: '',
      }));
      setSelectedDonationSchema(null);
      setSelectedTrustMaster(null);
      setSelectedTags([]);
      setTagsFromPledge(false);
      setDonationSchemaLocked(false); // Unlock schema for direct transactions
      setTrustMasterLocked(false); // Unlock trust master for direct transactions
      return;
    }

    setSelectedPledge(pledge);
    setFormData((prev) => ({
      ...prev,
      donation_id: pledge.id,
      donation_schema: pledge.donation_schema || '',
      trust_master_id: pledge.trust_master_id || '',
    }));

    // Set donation schema from pledge and lock it
    if (pledge.donation_schema) {
      const schema = donationSchemas.find((s) => s.id === pledge.donation_schema);
      setSelectedDonationSchema(schema || null);
    } else {
      setSelectedDonationSchema(null);
    }
    setDonationSchemaLocked(true); // Lock schema when pledge is selected

    // Set trust master from pledge and lock it
    if (pledge.trust_master_id) {
      const trustMaster = trustMasters.find((tm) => tm.id === pledge.trust_master_id);

      setSelectedTrustMaster(trustMaster || null);
    } else {
      setSelectedTrustMaster(null);
    }
    setTrustMasterLocked(true); // Lock trust master when pledge is selected

    // Set pledge summary
    const collectedAmount = pledge.collected_amount || 0;
    setPledgeSummary({
      amount: pledge.amount || 0,
      trust_name: pledge.trust_name || 'N/A',
      collected_amount: collectedAmount,
      remaining_amount: (pledge.amount || 0) - collectedAmount,
      completion_percentage:
        pledge.amount > 0 ? Math.round((collectedAmount / pledge.amount) * 100) : 0,
    });

    // Set tags from pledge if available
    if (pledge.tags && Array.isArray(pledge.tags) && pledge.tags.length > 0) {
      setSelectedTags(pledge.tags);
      setTagsFromPledge(true);
    } else if (pledge.tag_ids && Array.isArray(pledge.tag_ids) && pledge.tag_ids.length > 0) {
      const matchedTags = tags.filter((t) => pledge.tag_ids.includes(t.id));
      if (matchedTags.length > 0) {
        setSelectedTags(matchedTags);
        setTagsFromPledge(true);
      }
    } else {
      setTagsFromPledge(false);
    }
  };

  const handleContactChange = (_event, newValue) => {
    const newContactId = newValue?.id || '';
    setSelectedContact(newValue);
    setFormData((prev) => ({
      ...prev,
      contact_id: newContactId,
      donation_id: '', // Clear donation_id when contact changes
    }));

    // Clear pledge related state when contact changes
    setSelectedPledge(null);
    setPledgeSummary(null);
    setDonorPledges([]);
    setSelectedTags([]);
    setTagsFromPledge(false);

    // Clear search term when a contact is selected
    if (newValue) {
      setContactSearchTerm('');
      // Load pledges for this donor (only for direct transactions, not from reminder)
      if (!reminderId && !transactionId) {
        loadDonorPledges(newContactId);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.contact_id) {
      newErrors.contact_id = 'Please select a donor';
    }

    if (!formData.amount || parseInt(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    } else {
      // Check if amount exceeds remaining pledge amount (only if amount is valid)
      if (pledgeSummary && pledgeSummary.remaining_amount !== undefined) {
        const numValue = parseInt(formData.amount, 10);
        const remainingAmount = pledgeSummary.remaining_amount || 0;
        if (numValue > remainingAmount) {
          newErrors.amount = `Amount cannot exceed remaining pledge amount of ₹${remainingAmount.toLocaleString('en-IN')}`;
        }
      }
    }

    if (!formData.payment_date) {
      newErrors.payment_date = 'Please select a payment date';
    }

    if (!formData.payment_method) {
      newErrors.payment_method = 'Please select a payment method';
    }

    if (!formData.trust_master_id) {
      newErrors.trust_master_id = 'Trust is required';
    }

    setErrors(newErrors);

    // Return false if there are any errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        contact_id: formData.contact_id,
        amount: parseInt(formData.amount, 10),
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        transaction_reference: formData.transaction_reference || null,
        bank_name: formData.bank_name || null,
        bank_account_number: formData.bank_account_number || null,
        notes: formData.notes || null,
        tag_ids: selectedTags.map((tag) => tag.id), // Send array of tag IDs
        donation_schema: formData.donation_schema || null, // Include donation_schema
        trust_master_id: formData.trust_master_id || null, // Include trust_master_id
        receipt_sent_via_email: formData.receipt_sent_via_email,
        receipt_sent_via_whatsapp: formData.receipt_sent_via_whatsapp,
      };

      // Add timestamps if receipts are being sent
      if (formData.receipt_sent_via_email) {
        payload.receipt_email_sent_at = new Date().toISOString();
      }
      if (formData.receipt_sent_via_whatsapp) {
        payload.receipt_whatsapp_sent_at = new Date().toISOString();
      }

      // Add donation_id if present
      if (formData.donation_id) {
        payload.donation_id = formData.donation_id;
      }

      // Add reminder_id if creating from reminder
      if (reminderId) {
        payload.reminder_id = parseInt(reminderId);
      }

      if (transactionId) {
        // Edit mode - send editable fields including tags, donation_schema, and trust_master_id
        await transactionsAPI.update(transactionId, {
          amount: payload.amount,
          payment_date: payload.payment_date,
          notes: payload.notes,
          bank_name: payload.bank_name,
          bank_account_number: payload.bank_account_number,
          tag_ids: payload.tag_ids, // Include tags
          donation_schema: payload.donation_schema, // Include donation schema (only editable for direct transactions)
          trust_master_id: payload.trust_master_id, // Include trust master (only editable for direct transactions)
        });
      } else {
        // Create transaction (handles both direct and from-reminder cases)
        await transactionsAPI.create(payload);
      }

      onSuccess();
    } catch (error) {
      console.error('Transaction save error:', error);

      // Parse backend errors into field-level errors
      const backendErrors = parseBackendErrors(error);

      // Set field-level errors
      if (checkHasErrors(backendErrors)) {
        setErrors(backendErrors);
      }

      // Show general error message only if there's a _general error
      if (backendErrors._general) {
        showError(backendErrors._general);
      } else if (!checkHasErrors(backendErrors)) {
        // If no specific errors were parsed, show a generic error
        showError('Failed to save transaction');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getDisplayValue = (value, type = 'text') => {
    if (!value && value !== 0) return '—';

    switch (type) {
      case 'date':
        const dateStr = typeof value === 'string' ? value.split('T')[0].split(' ')[0] : value;
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      case 'currency':
        return `₹${parseFloat(value).toLocaleString('en-IN')}`;
      case 'payment_method':
        return value.replace('_', ' ');
      default:
        return value;
    }
  };

  const getContactName = () => {
    if (!selectedContact) return '—';
    if (selectedContact.type === 'INDIVIDUAL') {
      return `${selectedContact.first_name} ${selectedContact.last_name || ''}`.trim();
    }
    return selectedContact.organization_name || '—';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: '4px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        p: 3,
      }}
    >
      {isViewMode ? (
        // View Mode - Clean label-value display
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: colors.primary.main,
              mb: 2,
              pb: 1,
              borderBottom: `2px solid ${colors.primary.main}`,
            }}
          >
            Transaction Information
          </Typography>

          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Donor */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Donor
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.text.primary,
                      fontWeight: 500,
                      mt: 0.5,
                    }}
                  >
                    {getContactName()}
                  </Typography>
                </Box>

                {/* Amount */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Donated Amount
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: colors.text.primary,
                      fontWeight: 700,
                      mt: 0.5,
                    }}
                  >
                    {getDisplayValue(formData.amount, 'currency')}
                  </Typography>
                </Box>

                {/* Donation Scheme */}
                {!!formData.donation_schema_name && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Donation Scheme
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 700,
                        mt: 0.5,
                      }}
                    >
                      {formData.donation_schema_name}
                    </Typography>
                  </Box>
                )}

                {/* Trust Master */}
                {!!formData.trust_name && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Trust Master
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 500,
                        mt: 0.5,
                      }}
                    >
                      {formData.trust_name}
                    </Typography>
                  </Box>
                )}

                {/* Payment Date */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Payment Date
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.text.primary,
                      fontWeight: 500,
                      mt: 0.5,
                    }}
                  >
                    {getDisplayValue(formData.payment_date, 'date')}
                  </Typography>
                </Box>

                {/* Payment Method */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Payment Method
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.text.primary,
                      fontWeight: 500,
                      mt: 0.5,
                    }}
                  >
                    {paymentMethodLabel(formData.payment_method) || '--'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Bank Name */}
                {(formData.payment_method === 'BANK_TRANSFER' ||
                  formData.payment_method === 'CHEQUE') &&
                  formData.bank_name && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Bank Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {getDisplayValue(formData.bank_name)}
                      </Typography>
                    </Box>
                  )}

                {/* Bank Account Number */}
                {(formData.payment_method === 'BANK_TRANSFER' ||
                  formData.payment_method === 'CHEQUE') &&
                  formData.bank_account_number && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Bank Account Number
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {getDisplayValue(formData.bank_account_number)}
                      </Typography>
                    </Box>
                  )}

                {/* Transaction Reference */}
                {formData.transaction_reference && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Transaction Reference
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 500,
                        mt: 0.5,
                      }}
                    >
                      {getDisplayValue(formData.transaction_reference)}
                    </Typography>
                  </Box>
                )}

                {/* Notes */}
                {formData.notes && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Notes
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 500,
                        mt: 0.5,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {getDisplayValue(formData.notes)}
                    </Typography>
                  </Box>
                )}

                {/* Created By */}
                {formData.created_by_name && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Created By
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {getDisplayValue(formData.created_by_name)}
                    </Typography>
                  </Box>
                )}

                {/* Trust Master */}
                {formData.trust_name && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Trust
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {getDisplayValue(formData.trust_name)}
                    </Typography>
                  </Box>
                )}

                {/* Tags */}
                {selectedTags.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {selectedTags.length === 1 ? 'Tag' : 'Tags'}
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selectedTags.map((tag) => (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          size="small"
                          sx={{
                            backgroundColor: tag.color || 'rgba(139, 95, 191, 0.1)',
                            color: tag.color ? '#fff' : '#8B5FBF',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            borderRadius: '4px',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Pledge Summary Card */}
          {pledgeSummary && (
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: `1px solid ${colors.border.main}`,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary.main,
                  mb: 2,
                }}
              >
                Pledge Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Total Pledge
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ₹{pledgeSummary.amount?.toLocaleString('en-IN')}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Current Collected
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: colors.text.primary }}>
                    ₹{pledgeSummary.collected_amount?.toLocaleString('en-IN')}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Current Remaining
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: colors.text.primary }}>
                    ₹{pledgeSummary.remaining_amount?.toLocaleString('en-IN')}
                  </Typography>
                </Grid>
                {pledgeSummary.reminder_date && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                      Current Reminder Date
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: colors.primary.main }}
                    >
                      {new Date(pledgeSummary.reminder_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Box>
      ) : (
        // Edit Mode - Form fields
        <Box component="form" onSubmit={handleSubmit}>
          {/* Pledge Summary Card (shown when from reminder or editing transaction with pledge) */}
          {pledgeSummary && (
            <Card
              sx={{
                mb: 3,
                p: 3,
                borderRadius: '4px',
                backgroundColor: `${colors.primary.main}08`,
                border: `1px solid ${colors.primary.main}30`,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary.main,
                  mb: 2,
                }}
              >
                Pledge Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Pledged Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ₹{parseFloat(pledgeSummary.amount || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Collected Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ₹{parseFloat(pledgeSummary.collected_amount || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Remaining Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ₹{parseFloat(pledgeSummary.remaining_amount || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Trust
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {pledgeSummary.trust_name || 'N/A'}
                  </Typography>
                </Grid>
                {pledgeSummary.reminder_date && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                      Reminder Date
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: colors.primary.main }}
                    >
                      {new Date(pledgeSummary.reminder_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Card>
          )}

          <Grid container spacing={3}>
            {/* Donor Selection */}
            <Grid item xs={12} md={12}>
              <Autocomplete
                options={contacts}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  if (option.type === 'INDIVIDUAL') {
                    return `${option.first_name} ${option.last_name || ''}`.trim();
                  }
                  return option.organization_name || '';
                }}
                value={selectedContact}
                onChange={handleContactChange}
                onInputChange={(_event, newInputValue, reason) => {
                  // Update search term when user types
                  if (reason === 'input') {
                    setContactSearchTerm(newInputValue);
                  }
                  // When user clears the input, reset to show all contacts
                  if (reason === 'clear' || (reason === 'input' && !newInputValue)) {
                    setContactSearchTerm('');
                    setSelectedContact(null);
                  }
                }}
                filterOptions={(x) => x} // Disable client-side filtering - using server-side search
                noOptionsText={
                  loadingContacts
                    ? 'Searching...'
                    : contactSearchTerm
                      ? 'No contacts found'
                      : 'Type to search contacts'
                }
                loading={loadingContacts}
                disabled={lockedFields.contact_id}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (
                      !loadingContacts &&
                      hasMoreContacts &&
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight - 50
                    ) {
                      loadContacts(contactsPage + 1, debouncedContactSearch);
                    }
                  },
                  style: { maxHeight: '300px' },
                }}
                renderInput={(params) => (
                  <TextField
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    {...params}
                    label="Donor *"
                    placeholder={isViewMode ? '' : 'Type to search contacts...'}
                    disabled={isViewMode}
                    error={!!errors.contact_id}
                    helperText={
                      errors.contact_id ||
                      (lockedFields.contact_id ? 'Locked (cannot be changed)' : '')
                    }
                    InputProps={{
                      ...params.InputProps,
                      readOnly: isViewMode,
                      endAdornment: (
                        <>
                          {loadingContacts && !isViewMode && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Pledge Selection - Only show for direct transactions when donor is selected */}
            {!reminderId && !transactionId && selectedContact && (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Link to Pledge (Optional)"
                  value={selectedPledge?.id || 'none'}
                  onChange={(e) => {
                    const pledgeId = e.target.value;
                    if (pledgeId === 'none') {
                      handlePledgeSelect(null);
                    } else {
                      const pledge = donorPledges.find((p) => p.id === pledgeId);
                      handlePledgeSelect(pledge);
                    }
                  }}
                  disabled={loadingPledges}
                  helperText={
                    loadingPledges
                      ? 'Loading pledges...'
                      : donorPledges.length === 0
                        ? 'No active pledges for this donor'
                        : 'Select a pledge to auto-fill details'
                  }
                  FormHelperTextProps={{
                    sx: { marginLeft: 0 },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                >
                  <MenuItem value="none">No pledge</MenuItem>
                  {donorPledges.map((pledge, index) => (
                    <MenuItem key={pledge.id} value={pledge.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          gap: 2,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{ fontWeight: 800, color: colors.text.primary }}
                        >
                          Pledge {index + 1}
                          {pledge.donation_schema_name && ` - ${pledge.donation_schema_name}`}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ color: colors.text.primary, fontWeight: 200 }}
                        >
                          Pledge Date:{' '}
                          <Typography
                            component="span"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 600,
                            }}
                          >
                            {pledge.pledge_start_date}
                          </Typography>
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ color: colors.text.primary, fontWeight: 200 }}
                        >
                          Pledge amount: ₹
                          <Typography
                            component="span"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 600,
                            }}
                          >
                            {pledge.amount}
                          </Typography>
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 200,
                          }}
                        >
                          Frequency:{' '}
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 400,
                              backgroundColor: '#FEF3C7 !important',
                              color: '#92400E !important',
                              padding: '2px 4px',
                              borderRadius: '4px',
                              fontSize: '0.8rem !important',
                              mr: 12,
                            }}
                          >
                            {getFrequencyLabel(pledge.frequency)}
                          </Typography>
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Payment Date */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payment Date *"
                type="date"
                value={formData.payment_date}
                onClick={(e) => e.target.showPicker?.()}
                onChange={(e) => handleChange('payment_date', e.target.value)}
                error={!!errors.payment_date}
                helperText={errors.payment_date}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
                InputLabelProps={{ shrink: true }}
                disabled={isViewMode}
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Donation Amount *"
                type="text"
                inputMode="numeric"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                error={!!errors.amount}
                helperText={errors.amount}
                placeholder="Enter amount (numbers only)"
                disabled={isViewMode}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
              />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Payment Method *"
                value={formData.payment_method}
                onChange={(e) => handleChange('payment_method', e.target.value)}
                error={!!errors.payment_method}
                helperText={errors.payment_method}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
                disabled={transactionId || isViewMode} // Disable in edit mode or view mode
              >
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="CHEQUE">Cheque</MenuItem>
                <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                <MenuItem value="UPI">UPI</MenuItem>
                <MenuItem value="CARD">Card</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>

            {/* Transaction Reference */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Transaction Reference"
                value={formData.transaction_reference}
                onChange={(e) => handleChange('transaction_reference', e.target.value)}
                placeholder="Cheque no, UPI ref, etc."
                disabled={transactionId || isViewMode} // Disable in edit mode or view mode
              />
            </Grid>

            {/* Bank Name - Show only for BANK_TRANSFER, CHEQUE */}
            {(formData.payment_method === 'BANK_TRANSFER' ||
              formData.payment_method === 'CHEQUE') && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={formData.bank_name}
                  onChange={(e) => handleChange('bank_name', e.target.value)}
                  placeholder="Enter bank name"
                  disabled={isViewMode}
                />
              </Grid>
            )}

            {/* Bank Account Number - Show only for BANK_TRANSFER, CHEQUE */}
            {(formData.payment_method === 'BANK_TRANSFER' ||
              formData.payment_method === 'CHEQUE') && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Account Number"
                  value={formData.bank_account_number}
                  onChange={(e) => handleChange('bank_account_number', e.target.value)}
                  placeholder="Enter account number"
                  disabled={isViewMode}
                />
              </Grid>
            )}

            {/* Trust Master - Auto-filled from pledge/reminder, selectable for new transactions */}
            <Grid item xs={12} md={12}>
              <Autocomplete
                options={trustMasters}
                getOptionLabel={(option) => option.trustName || option.trust_name || ''}
                value={selectedTrustMaster}
                onChange={(event, newValue) => {
                  setSelectedTrustMaster(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    trust_master_id: newValue?.id || '',
                  }));
                  // Clear trust_master_id error when user selects a trust
                  if (newValue) {
                    setErrors((prev) => ({ ...prev, trust_master_id: '' }));
                  }
                  // Clear search term when a trust is selected
                  if (newValue) {
                    setTrustMasterSearchTerm('');
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Update search term when user types
                  if (reason === 'input') {
                    setTrustMasterSearchTerm(newInputValue);
                  }
                  // When user clears the input, reset to show all trusts
                  if (reason === 'clear' || (reason === 'input' && !newInputValue)) {
                    setTrustMasterSearchTerm('');
                    setSelectedTrustMaster(null);
                  }
                }}
                noOptionsText={
                  loadingTrustMasters
                    ? 'Searching...'
                    : trustMasterSearchTerm
                      ? 'No trusts found'
                      : 'Type to search trusts'
                }
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight - 50 &&
                      hasMoreTrustMasters &&
                      !loadingTrustMasters
                    ) {
                      loadTrustMasters(trustMastersPage + 1, debouncedTrustMasterSearch);
                    }
                  },
                }}
                disabled={trustMasterLocked || isViewMode}
                loading={loadingTrustMasters}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Trust *"
                    placeholder={isViewMode ? '' : 'Type to search trusts...'}
                    disabled={trustMasterLocked || isViewMode}
                    error={!!errors.trust_master_id}
                    helperText={
                      errors.trust_master_id ||
                      (trustMasterLocked
                        ? 'Trust master inherited from pledge (cannot be changed)'
                        : '')
                    }
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: trustMasterLocked || isViewMode,
                      endAdornment: (
                        <>
                          {loadingTrustMasters && !isViewMode && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Donation Schema - Optional for manual transactions, locked for reminder-based */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={donationSchemas}
                getOptionLabel={(option) => option.name || ''}
                value={selectedDonationSchema}
                onChange={(event, newValue) => {
                  setSelectedDonationSchema(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    donation_schema: newValue?.id || '',
                  }));
                }}
                disabled={donationSchemaLocked || isViewMode}
                loading={loadingSchemas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Donation Scheme (optional)"
                    placeholder={isViewMode ? '' : 'Select donation scheme...'}
                    disabled={donationSchemaLocked || isViewMode}
                    helperText={
                      donationSchemaLocked
                        ? 'Donation scheme is locked (transaction from pledge/reminder)'
                        : 'Optional - Select a donation scheme'
                    }
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: donationSchemaLocked || isViewMode,
                      endAdornment: (
                        <>
                          {loadingSchemas && !isViewMode && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Tag Dropdown */}
            <Grid item xs={12} md={6}>
              <Box>
                <TagAutocomplete
                  options={tags}
                  value={selectedTags}
                  onChange={(event, newValue) => {
                    // newValue is an array of tag objects when multiple={true}
                    setSelectedTags(newValue || []);
                  }}
                  multiple={true}
                  placeholder="Select tags (optional)"
                  loading={tagsStatus === 'loading'}
                  autocompleteProps={{
                    disabled: isViewMode || (!!reminderId && tagsFromPledge),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      minHeight: '56px',
                    },
                  }}
                />
                {reminderId && selectedTags.length > 0 && tagsFromPledge && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      mt: 0.5,
                      display: 'block',
                    }}
                  >
                    Tags inherited from pledge (cannot be changed)
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional notes..."
                disabled={isViewMode}
              />
            </Grid>

            {/* Receipt Checkboxes - Only show in create mode */}
            {!transactionId && (
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      fontWeight: 500,
                      mb: 1,
                      fontSize: '1rem',
                    }}
                  >
                    Send Receipt
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 4,
                      alignItems: 'center',
                    }}
                  >
                    <FormControlLabel
                      disabled={
                        !selectedContact?.primary_email || !selectedTrustMaster?.emailTemplateId
                      }
                      control={
                        <Checkbox
                          checked={formData.receipt_sent_via_email}
                          disabled={
                            !selectedContact?.primary_email || !selectedTrustMaster?.emailTemplateId
                          }
                          onChange={(e) => handleChange('receipt_sent_via_email', e.target.checked)}
                          sx={{
                            color: colors.text.secondary,
                            '&.Mui-checked': {
                              color: colors.primary.main,
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 28,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.975rem',
                              color:
                                selectedContact?.primary_email &&
                                selectedTrustMaster?.emailTemplateId
                                  ? colors.text.primary
                                  : colors.text.disabled,
                            }}
                          >
                            Send via Email
                          </Typography>
                          {!selectedContact?.primary_email && selectedContact && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '0.75rem',
                                color: colors.text.disabled,
                                display: 'block',
                              }}
                            >
                              (No email available)
                            </Typography>
                          )}
                          {selectedContact?.primary_email &&
                            selectedTrustMaster &&
                            !selectedTrustMaster.emailTemplateId && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '0.75rem',
                                  color: colors.text.disabled,
                                  display: 'block',
                                }}
                              >
                                (No email template configured in trust)
                              </Typography>
                            )}
                        </Box>
                      }
                    />
                    <FormControlLabel
                      disabled={
                        !selectedContact?.phone_1 || !selectedTrustMaster?.whatsappTemplateId
                      }
                      control={
                        <Checkbox
                          checked={formData.receipt_sent_via_whatsapp}
                          disabled={
                            !selectedContact?.phone_1 || !selectedTrustMaster?.whatsappTemplateId
                          }
                          onChange={(e) =>
                            handleChange('receipt_sent_via_whatsapp', e.target.checked)
                          }
                          sx={{
                            color: colors.text.secondary,
                            '&.Mui-checked': {
                              color: colors.primary.main,
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 28,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.975rem',
                              color:
                                selectedContact?.phone_1 && selectedTrustMaster?.whatsappTemplateId
                                  ? colors.text.primary
                                  : colors.text.disabled,
                            }}
                          >
                            Send via WhatsApp
                          </Typography>
                          {!selectedContact?.phone_1 && selectedContact && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '0.75rem',
                                color: colors.text.disabled,
                                display: 'block',
                              }}
                            >
                              (No phone number available)
                            </Typography>
                          )}
                          {selectedContact?.phone_1 &&
                            selectedTrustMaster &&
                            !selectedTrustMaster.whatsappTemplateId && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '0.75rem',
                                  color: colors.text.disabled,
                                  display: 'block',
                                }}
                              >
                                (No WhatsApp template configured in trust)
                              </Typography>
                            )}
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Action Buttons - Only show in edit mode */}
            {!isViewMode && (
              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={submitting}
                    sx={{
                      borderColor: colors.text.secondary,
                      color: colors.text.secondary,
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: colors.text.primary,
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      borderRadius: '4px',
                      backgroundColor: colors.primary.main,
                      '&:hover': { backgroundColor: colors.primary.dark },
                    }}
                  >
                    {submitting ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : transactionId ? (
                      'Update Transaction'
                    ) : (
                      'Create Transaction'
                    )}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Card>
  );
});

export default TransactionForm;
